import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, Check, X, Search, Zap, Eye, MessageSquare, 
  FileText, Share2, Copy, Code, ExternalLink, HelpCircle,
  TrendingUp, Clock, Shield, Star, Users, Globe, ArrowUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { UpgradeButton } from '@/components/PaymentHandler';
import { useAuth } from '@/context/AuthContext';
import Footer, { PreFooterCTA } from '@/components/Footer';
import { useTranslation } from 'react-i18next';

const BASE_URL = window.location.origin;

// Scroll to top button component
function ScrollToTop() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 500) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={scrollToTop}
          className="fixed bottom-24 right-6 z-[101] w-12 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
          aria-label="Przewiń do góry"
        >
          <ArrowUp className="w-5 h-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

// FAQ data for schema
const faqData = [
  {
    question: "Czy muszę mieć własną stronę?",
    answer: "Nie. ApisAI daje gotową stronę hodowli i miotu na Twój link. Możesz ją udostępniać na FB, OLX czy w wiadomościach."
  },
  {
    question: "Czy mogę udostępniać na Facebooku?",
    answer: "Tak. Dostajesz jeden link do wklejenia. Post na FB ma sens, bo link prowadzi do strony miotu z pełnymi informacjami."
  },
  {
    question: "Czy to działa dla innych organizacji niż ZKwP?",
    answer: "Tak. System jest neutralny — działa dla FCI, ZKwP oraz innych organizacji krajowych i międzynarodowych."
  },
  {
    question: "Ile to zajmuje?",
    answer: "Około 10 minut na pierwszą stronę miotu. Dodajesz hodowlę, potem miot — i masz gotowy link."
  },
  {
    question: "Czy mogę anulować?",
    answer: "Tak. Anulowanie jest możliwe w każdej chwili. System działa do końca opłaconego okresu."
  },
  {
    question: "Za co płacę?",
    answer: "Płacisz za nielimitowane mioty, galerię zdjęć, narzędzia AI do generowania opisów i priorytetowe indeksowanie w Google."
  },
  {
    question: "Czy leady są w panelu?",
    answer: "Tak. Wszystkie zapytania z formularza na stronie miotu automatycznie zapisują się w Twoim panelu. Nic nie musisz instalować."
  },
  {
    question: "Czy mogę wstawić na moją stronę?",
    answer: "Tak. Możesz dodać przycisk linkujący do strony miotu lub osadzić całą stronę przez iframe."
  }
];

// Schema.org FAQPage
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

export default function Oferta() {
  const { t } = useTranslation();
  return (
    <>
      <Helmet>
        <title>Oferta dla Hodowców — ApisAI | Sprzedaj miot szybciej</title>
        <meta name="description" content="Stwórz profesjonalną stronę hodowli i miotu w 10 minut. SEO, formularze, gotowe linki do FB/OLX. Bez techniki, bez agencji." />
        <meta property="og:title" content="Oferta dla Hodowców — ApisAI" />
        <meta property="og:description" content="Sprzedaj miot szybciej. Zbuduj widoczność hodowli w Google i AI — automatycznie." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${BASE_URL}/oferta`} />
        <link rel="canonical" href={`${BASE_URL}/oferta`} />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0B0F1A]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0F1A]/80 backdrop-blur-md border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 md:gap-3">
              <img src="/logo.svg" alt="ApisAI" className="w-8 h-8 md:w-10 md:h-10" />
              <span className="font-semibold text-lg md:text-xl text-white">ApisAI</span>
            </Link>
            <div className="flex items-center gap-2 md:gap-4">
              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" className="text-gray-300 hover:text-white">
                  Zaloguj się
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-blue-500 hover:bg-blue-600 rounded-full px-4 md:px-6 text-sm md:text-base">
                  Rozpocznij
                </Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* HERO */}
        <section className="pt-32 pb-20 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-6 bg-blue-500/20 text-blue-400 border-blue-500/30 px-4 py-1">
                Dla hodowców psów i kotów
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Sprzedaj miot szybciej.<br />
                <span className="text-blue-400">Zbuduj widoczność w Google i AI</span> — automatycznie.
              </h1>
              
              <p className="text-xl text-gray-400 mb-8 max-w-3xl mx-auto">
                ApisAI tworzy premium stronę hodowli i miotu + gotowe opisy + posty. 
                Bez budowania strony, bez techniki, bez agencji.
                <br />
                <span className="text-white font-medium">Masz link gotowy do wklejenia na FB/OLX w 10 minut.</span>
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-blue-500 hover:bg-blue-600 rounded-full px-8 py-6 text-lg font-semibold"
                  >
                    Uruchom ApisAI (za darmo)
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/hodowla/demo">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="rounded-full px-8 py-6 text-lg border-white/20 hover:bg-white/5"
                  >
                    Zobacz demo hodowli
                  </Button>
                </Link>
              </div>

              {/* Proof line */}
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  start w 10 minut
                </span>
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  bez techniki
                </span>
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  gotowe do FB/OLX
                </span>
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  SEO i AI ready
                </span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent to-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Dziś nie wygrywa najlepszy pies.<br />
                <span className="text-blue-400">Wygrywa hodowla, która jest widoczna.</span>
              </h2>
            </motion.div>

            <div className="space-y-6">
              {[
                { icon: Search, text: 'Kupujący nie chce szukać w grupach — wpisuje w Google: "owczarek niemiecki długowłosy szczeniaki mazowieckie"' },
                { icon: Clock, text: 'Facebook nie buduje SEO. Post znika po 24h.' },
                { icon: TrendingUp, text: 'Jeżeli nie masz strony miotu — tracisz leady na rzecz hodowli, które mają gotowe linki.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-4 p-6 rounded-2xl bg-white/5 border border-white/10"
                >
                  <item.icon className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300 text-lg">{item.text}</p>
                </motion.div>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mt-12 text-xl text-blue-400 font-medium"
            >
              ApisAI robi z Twojego miotu &ldquo;produkt online&rdquo; — gotowy do wyszukiwarki.
            </motion.p>
          </div>
        </section>

        {/* How it works - 3 steps */}
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-white text-center mb-16"
            >
              Jak to działa?
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: 1,
                  title: "Dodajesz hodowlę",
                  desc: "Rasa, miasto, organizacja (ZKwP/FCI lub inne)",
                  icon: Users
                },
                {
                  step: 2,
                  title: "Dodajesz miot",
                  desc: "Rodzice, daty, liczby szczeniąt, cena, status",
                  icon: FileText
                },
                {
                  step: 3,
                  title: "Dostajesz gotowy link",
                  desc: "Strona miotu z SEO + formularz zapytań. Jeden link do FB/OLX.",
                  icon: Share2
                }
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="relative p-8 rounded-3xl bg-gradient-to-b from-white/10 to-white/5 border border-white/10"
                >
                  <div className="absolute -top-4 left-8 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                    {item.step}
                  </div>
                  <item.icon className="w-10 h-10 text-blue-400 mb-4 mt-2" />
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link to="/miot/miot-demo">
                <Button variant="outline" className="rounded-full border-white/20 hover:bg-white/5">
                  Zobacz jak wygląda link demo
                  <ExternalLink className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent to-blue-500/5">
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-white text-center mb-16"
            >
              Co zyskujesz jako hodowca
            </motion.h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: MessageSquare,
                  title: "Więcej zapytań o miot",
                  desc: "Bo kupujący widzi jasną stronę, a nie chaos ogłoszeń."
                },
                {
                  icon: Zap,
                  title: "Sprzedaż szybciej",
                  desc: "Strona od razu odpowiada na pytania: cena, daty, dostępność, rodzice."
                },
                {
                  icon: Globe,
                  title: "Widoczność w Google + AI",
                  desc: "Strony miotów są indeksowane (SEO) i zrozumiałe dla AI (schema)."
                },
                {
                  icon: Star,
                  title: "Profesjonalny wizerunek premium",
                  desc: "Twoja hodowla wygląda jak marka, nie jak ogłoszenie."
                },
                {
                  icon: Shield,
                  title: 'Mniej wiadomości "ile kosztuje?"',
                  desc: "Formularz zbiera konkret: płeć, preferencje, telefon."
                },
                {
                  icon: Share2,
                  title: "Gotowe posty do FB/OLX",
                  desc: "Jedno kliknięcie → opis + CTA + hashtag."
                }
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-blue-500/30 transition-colors"
                >
                  <item.icon className="w-8 h-8 text-blue-400 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-white text-center mb-6"
            >
              Wszystko, czego potrzebujesz
            </motion.h2>
            <p className="text-gray-400 text-center mb-12 text-lg">
              żeby sprzedać miot bez stresu
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                "Premium strona hodowli + profil publiczny",
                "Premium strona miotu (SEO/OG/canonical/schema)",
                "Formularz zapytań (lead) + panel statusów",
                "Galeria: cover + zdjęcia (lightbox)",
                "Sekcje: rodzice, zdrowie/badania, daty, warunki odbioru",
                "Strony ras (programmatic SEO)",
                "Sitemap + IndexNow (szybkie indeksowanie)",
                "Generator opisów hodowli i miotu (AI)"
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 p-4 rounded-xl bg-white/5"
                >
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">{feature}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent to-white/5 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-white text-center mb-16"
            >
              Zacznij zdobywać klientów
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto">
              {/* FREE */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02, y: -4 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="group p-6 lg:p-8 rounded-3xl bg-white/5 border border-white/10 flex flex-col relative overflow-hidden cursor-pointer hover:border-white/20 hover:shadow-[0_0_40px_rgba(255,255,255,0.05)] transition-all duration-300"
              >
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/0 group-hover:from-white/5 group-hover:to-transparent transition-all duration-500 rounded-3xl" />
                
                <div className="relative">
                  <h3 className="text-xl font-semibold text-white mb-2">FREE</h3>
                  <p className="text-4xl font-bold text-white mb-1">0 zł</p>
                  <p className="text-gray-400 text-sm mb-2">na zawsze</p>
                  {/* Core value prop */}
                  <p className="text-green-400 text-sm font-medium mb-6 border-t border-white/10 pt-3">
                    Wizytówka hodowli w Google i AI
                  </p>
                  <ul className="space-y-3 mb-8 flex-1">
                    {[
                      { text: "Profil hodowli (publiczny)", included: true },
                      { text: "Prezentacja psów / rodziców", included: true },
                      { text: "Kontakt (tel/mail/FB)", included: true },
                      { text: "Link do udostępniania", included: true },
                      { text: "SEO basic", included: true },
                      { text: "Publiczne strony miotów", included: false },
                    ].map((f, i) => (
                      <li key={i} className={`flex items-center gap-2 text-sm lg:text-base ${f.included ? 'text-gray-300' : 'text-gray-500 line-through'}`}>
                        {f.included ? (
                          <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                        ) : (
                          <X className="w-4 h-4 text-gray-600 flex-shrink-0" />
                        )}
                        {f.text}
                      </li>
                    ))}
                  </ul>
                  <div className="space-y-2">
                    <Link to="/register">
                      <Button variant="outline" className="w-full rounded-full border-white/20 hover:bg-white/10 group-hover:border-white/40 transition-all">
                        Zacznij za darmo
                      </Button>
                    </Link>
                    <p className="text-center text-xs text-gray-500">bez karty, bez zobowiązań</p>
                  </div>
                </div>
              </motion.div>

              {/* PRO - Featured */}
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.03, y: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
                className="group p-6 lg:p-8 pt-10 rounded-3xl bg-gradient-to-b from-blue-500/20 to-blue-500/5 border-2 border-blue-500/50 relative flex flex-col overflow-visible cursor-pointer hover:border-blue-400 hover:shadow-[0_0_60px_rgba(59,130,246,0.3)] transition-all duration-300 md:-mt-4 md:mb-4"
              >
                {/* Animated glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-blue-500/0 group-hover:from-blue-500/10 group-hover:to-transparent transition-all duration-500 rounded-3xl" />
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl group-hover:bg-blue-500/30 transition-all duration-500" />
                
                {/* Popular badge with pulse */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <div className="relative">
                    <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-20" />
                    <span className="relative px-4 py-1.5 bg-blue-500 rounded-full text-sm font-medium text-white whitespace-nowrap shadow-lg">
                      Dla sprzedających
                    </span>
                  </div>
                </div>
                
                <div className="relative pt-2">
                  <h3 className="text-xl font-semibold text-white mb-2">PRO</h3>
                  <p className="text-4xl font-bold text-white mb-1">49 zł</p>
                  <p className="text-blue-300 text-sm mb-2">/miesiąc</p>
                  {/* Core value prop */}
                  <p className="text-blue-400 text-sm font-medium mb-6 border-t border-blue-500/30 pt-3">
                    Sprzedaż miotu z formularzem leadów
                  </p>
                  <ul className="space-y-3 mb-8 flex-1">
                    {[
                      "Wszystko z FREE",
                      "1 aktywny miot (publiczny)",
                      "Strona miotu z SEO",
                      "Formularz zapytań (leady)",
                      "Galeria zdjęć miotu",
                      "Podstawowe indeksowanie",
                    ].map((f, i) => (
                      <motion.li 
                        key={i} 
                        className="flex items-center gap-2 text-gray-200 text-sm lg:text-base"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 + i * 0.05 }}
                      >
                        <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        {f}
                      </motion.li>
                    ))}
                  </ul>
                  <div className="space-y-2">
                    <UpgradeButton plan="pro" className="w-full font-semibold shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40">
                      Aktywuj PRO
                      <ChevronRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </UpgradeButton>
                    <p className="text-center text-xs text-blue-300/70">anulujesz w każdej chwili</p>
                  </div>
                </div>
              </motion.div>

              {/* PREMIUM */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02, y: -4 }}
                transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.2 }}
                className="group p-6 lg:p-8 rounded-3xl bg-gradient-to-b from-amber-500/10 to-amber-500/5 border border-amber-500/20 flex flex-col relative overflow-hidden cursor-pointer hover:border-amber-500/40 hover:shadow-[0_0_40px_rgba(245,158,11,0.15)] transition-all duration-300"
              >
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-500/0 group-hover:from-amber-500/5 group-hover:to-transparent transition-all duration-500 rounded-3xl" />
                
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-semibold text-white">PREMIUM</h3>
                    <span className="text-amber-400">👑</span>
                  </div>
                  <p className="text-4xl font-bold text-white mb-1">99 zł</p>
                  <p className="text-amber-300 text-sm mb-2">/miesiąc</p>
                  {/* Core value prop */}
                  <p className="text-amber-400 text-sm font-medium mb-6 border-t border-amber-500/20 pt-3">
                    Nieograniczone mioty + AI boost + white-label
                  </p>
                  <ul className="space-y-3 mb-8 flex-1">
                    {[
                      "Wszystko z PRO",
                      "Nielimitowane mioty",
                      "AI generator opisów",
                      "Priorytetowe indeksowanie",
                      "White-label (brak brandingu)",
                      "Wsparcie priorytetowe",
                    ].map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-300 text-sm lg:text-base">
                        <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="space-y-2">
                    <UpgradeButton plan="premium" className="w-full">
                      Aktywuj PREMIUM
                    </UpgradeButton>
                    <p className="text-center text-xs text-amber-400/60">anulujesz w każdej chwili</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* CTA after pricing */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <p className="text-gray-400 mb-4">Nie wiesz, który plan wybrać?</p>
              <Link to="/register">
                <Button size="lg" className="bg-blue-500 hover:bg-blue-600 rounded-full px-8">
                  Zacznij od FREE — upgrade w każdej chwili
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* How to implement */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-white text-center mb-16"
            >
              Jak wdrożyć?
            </motion.h2>

            <div className="space-y-8">
              {/* Option 1: Link */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-8 rounded-3xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Copy className="w-6 h-6 text-blue-400" />
                  <h3 className="text-xl font-semibold text-white">Opcja 1: Skopiuj link (najprostsze)</h3>
                </div>
                <p className="text-gray-400 mb-4">
                  W panelu kliknij &ldquo;Udostępnij&rdquo; i skopiuj link do miotu lub hodowli. Wklej w:
                </p>
                <ul className="space-y-2 text-gray-300 mb-4">
                  <li>• Post na Facebooku</li>
                  <li>• Ogłoszenie OLX</li>
                  <li>• Wiadomość Messenger / WhatsApp</li>
                </ul>
                <div className="p-4 rounded-xl bg-black/30 font-mono text-sm text-gray-300">
                  https://apisai.pl/miot/twoj-miot-slug
                </div>
              </motion.div>

              {/* Option 2: Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-8 rounded-3xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3 mb-4">
                  <ExternalLink className="w-6 h-6 text-blue-400" />
                  <h3 className="text-xl font-semibold text-white">Opcja 2: Przycisk na stronie</h3>
                </div>
                <p className="text-gray-400 mb-4">
                  Wklej ten kod HTML na swoją stronę WWW:
                </p>
                <div className="p-4 rounded-xl bg-black/30 font-mono text-sm text-gray-300 overflow-x-auto">
                  {`<a href="https://apisai.pl/miot/twoj-slug" target="_blank" style="background:#3B82F6;color:#fff;padding:12px 18px;border-radius:12px;text-decoration:none;font-weight:600;">Zobacz miot</a>`}
                </div>
              </motion.div>

              {/* Option 3: Iframe */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-8 rounded-3xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Code className="w-6 h-6 text-blue-400" />
                  <h3 className="text-xl font-semibold text-white">Opcja 3: Osadź stronę (iframe)</h3>
                </div>
                <p className="text-gray-400 mb-4">
                  Możesz osadzić całą stronę miotu na swojej stronie:
                </p>
                <div className="p-4 rounded-xl bg-black/30 font-mono text-sm text-gray-300 overflow-x-auto">
                  {`<iframe src="https://apisai.pl/miot/twoj-slug" style="width:100%;height:900px;border:0;border-radius:16px;" loading="lazy"></iframe>`}
                </div>
              </motion.div>
            </div>

            <p className="text-center text-gray-400 mt-8">
              <Shield className="w-4 h-4 inline mr-2" />
              Leady z formularza automatycznie zapisują się w Twoim panelu. Nic nie musisz instalować.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent to-white/5">
          <div className="max-w-3xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-white text-center mb-12"
            >
              Często zadawane pytania
            </motion.h2>

            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, i) => (
                <AccordionItem 
                  key={i} 
                  value={`item-${i}`}
                  className="bg-white/5 border border-white/10 rounded-2xl px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-white hover:text-blue-400 py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Pre-footer CTA */}
        <PreFooterCTA />

        {/* Footer */}
        <Footer />
        
        {/* Scroll to top button */}
        <ScrollToTop />
      </div>
    </>
  );
}
